import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
    XMarkIcon,
    PlayIcon,
    StopIcon,
    ArrowUturnLeftIcon,
    MegaphoneIcon,
    PlusIcon,
    MinusIcon,
    ChevronDownIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/solid';
import { ALLOCATION, MAX_SUBSESSIONS } from '../../../hooks/subsessionHelpers';
import { SubsessionsPanelBones } from './SubsessionsPanelBones';

const MAIN_SESSION = 'main';
const COUNT_PRESETS = [2, 3, 4, 5, 6];

const initialsFrom = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const roomStatusText = (subsessions) => {
    if (subsessions.isOpen) return 'Abierta';
    if (subsessions.isClosing) return 'Cerrando…';
    return 'Cerrada';
};

const Avatar = ({ name }) => (
    <span className="vc-bo__avatar" aria-hidden="true">
        {initialsFrom(name)}
    </span>
);

const MemberRow = ({
    member,
    subsessionId,
    rooms,
    isOpen,
    busy,
    onAssign,
}) => (
    <li className="vc-bo__member">
        <span className="vc-bo__tree-rail" aria-hidden="true" />
        <Avatar name={member.displayName} />
        <span className="vc-bo__member-name" title={member.displayName}>
            {member.displayName}
        </span>
        {member.isInSubsession != null && (
            <span
                className={`vc-bo__dot ${member.isInSubsession ? 'is-in' : ''}`}
                aria-hidden="true"
                title={member.isInSubsession ? 'En el grupo' : 'Asignado, aún en sala principal'}
            />
        )}
        <label className="vc-bo__sr-only" htmlFor={`move-${subsessionId}-${member.userId}`}>
            Mover a {member.displayName}
        </label>
        <select
            id={`move-${subsessionId}-${member.userId}`}
            className="vc-bo__select"
            value=""
            disabled={busy}
            aria-label={`Mover a ${member.displayName}`}
            onChange={(e) => {
                onAssign(member.userId, e.target.value);
                e.target.value = '';
            }}
        >
            <option value="">Mover</option>
            {rooms
                .filter((s) => s.subsessionId !== subsessionId)
                .map((s) => (
                    <option key={s.subsessionId} value={s.subsessionId}>
                        {s.subsessionName}
                    </option>
                ))}
            {isOpen && <option value={MAIN_SESSION}>Sala principal</option>}
        </select>
    </li>
);

const CreateStep = ({
    count,
    setCount,
    pattern,
    setPattern,
    busy,
    onCreate,
}) => {
    const previewNames = useMemo(
        () => Array.from({ length: count }, (_, i) => `Sala ${i + 1}`),
        [count],
    );

    return (
        <section className="vc-bo__section vc-bo__create" aria-labelledby="vc-bo-create-title">
            <h4 id="vc-bo-create-title">Crear salas</h4>
            <p className="vc-bo__lead">
                Elige cuántas salas necesitas y cómo se asignarán los participantes.
            </p>

            <div className="vc-bo__step">
                <span className="vc-bo__step-label">Cantidad</span>
                <div className="vc-bo__count-row">
                    <button
                        type="button"
                        className="vc-bo__icon-btn"
                        disabled={busy || count <= 1}
                        aria-label="Menos salas"
                        onClick={() => setCount((c) => Math.max(1, c - 1))}
                    >
                        <MinusIcon aria-hidden="true" />
                    </button>
                    <output className="vc-bo__count-value" htmlFor="vc-bo-count" aria-live="polite">
                        {count}
                    </output>
                    <button
                        type="button"
                        className="vc-bo__icon-btn"
                        disabled={busy || count >= MAX_SUBSESSIONS}
                        aria-label="Más salas"
                        onClick={() => setCount((c) => Math.min(MAX_SUBSESSIONS, c + 1))}
                    >
                        <PlusIcon aria-hidden="true" />
                    </button>
                </div>
                <div className="vc-bo__presets" role="group" aria-label="Atajos de cantidad">
                    {COUNT_PRESETS.map((n) => (
                        <button
                            key={n}
                            type="button"
                            className={`vc-bo__chip ${count === n ? 'is-active' : ''}`}
                            disabled={busy}
                            aria-pressed={count === n}
                            onClick={() => setCount(n)}
                        >
                            {n}
                        </button>
                    ))}
                </div>
                <input
                    id="vc-bo-count"
                    className="vc-bo__sr-only"
                    type="number"
                    name="subsessionCount"
                    min={1}
                    max={MAX_SUBSESSIONS}
                    value={count}
                    autoComplete="off"
                    onChange={(e) => setCount(Math.max(
                        1,
                        Math.min(MAX_SUBSESSIONS, Number(e.target.value) || 1),
                    ))}
                />
                <p className="vc-bo__meta">Máximo {MAX_SUBSESSIONS} salas</p>
            </div>

            <fieldset className="vc-bo__step vc-bo__pattern" disabled={busy}>
                <legend className="vc-bo__step-label">Asignación</legend>
                <label className={`vc-bo__choice ${pattern === ALLOCATION.MANUAL ? 'is-active' : ''}`}>
                    <input
                        type="radio"
                        name="allocation"
                        value={ALLOCATION.MANUAL}
                        checked={pattern === ALLOCATION.MANUAL}
                        onChange={() => setPattern(ALLOCATION.MANUAL)}
                    />
                    <span>
                        <strong>Manual</strong>
                        <small>Tú decides quién va a cada sala</small>
                    </span>
                </label>
                <label className={`vc-bo__choice ${pattern === ALLOCATION.AUTO ? 'is-active' : ''}`}>
                    <input
                        type="radio"
                        name="allocation"
                        value={ALLOCATION.AUTO}
                        checked={pattern === ALLOCATION.AUTO}
                        onChange={() => setPattern(ALLOCATION.AUTO)}
                    />
                    <span>
                        <strong>Automática</strong>
                        <small>Se reparte en partes iguales</small>
                    </span>
                </label>
            </fieldset>

            <div className="vc-bo__preview" aria-label="Vista previa de nombres">
                {previewNames.map((name) => (
                    <span key={name} className="vc-bo__preview-pill">{name}</span>
                ))}
            </div>

            <button
                type="button"
                className="vc-bo__btn is-primary is-block"
                disabled={busy}
                onClick={onCreate}
            >
                {busy ? 'Creando…' : `Crear ${count} ${count === 1 ? 'sala' : 'salas'}`}
            </button>
        </section>
    );
};

export const SubsessionsPanel = ({ subsessions, onClose }) => {
    const [count, setCount] = useState(2);
    const [pattern, setPattern] = useState(ALLOCATION.MANUAL);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [collapsed, setCollapsed] = useState({});
    const [showBroadcast, setShowBroadcast] = useState(false);
    const unassignedRef = useRef(null);

    useEffect(() => {
        setCount(2);
        setPattern(ALLOCATION.MANUAL);
        setMessage('');
        setBusy(false);
        setCollapsed({});
        setShowBroadcast(false);
    }, [subsessions.setupGeneration]);

    const run = async (action, successMessage) => {
        if (busy) return;
        setBusy(true);
        try {
            await action();
            if (successMessage) toast.success(successMessage);
        } catch (err) {
            toast.error(err?.message || 'No se pudo completar la acción');
        } finally {
            setBusy(false);
        }
    };

    const rooms = subsessions.subsessions || [];
    const isBusy = busy || Boolean(subsessions.actionPending);
    const hasSubsessions = !subsessions.forceCreateUi && (
        rooms.length > 0
        || subsessions.isOpen
        || subsessions.isClosing
    );
    const unassigned = subsessions.unassigned || [];
    const roomStatus = roomStatusText(subsessions);

    const handleAssign = (userId, targetId) => {
        if (!targetId) return;
        if (targetId === MAIN_SESSION) {
            run(() => subsessions.moveUserToMainSession(userId));
            return;
        }
        run(() => subsessions.assignUser(userId, targetId));
    };

    const focusAssign = () => {
        if (!unassigned.length) {
            toast('Todos los participantes ya están asignados', { icon: '✅' });
            return;
        }
        unassignedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const toggleRoom = (id) => {
        setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const blockCreate = subsessions.supported === false && !hasSubsessions;

    return (
        <aside className="vc-bo" role="dialog" aria-modal="false" aria-labelledby="vc-bo-title">
            <header className="vc-bo__header">
                <h2 className="vc-bo__title" id="vc-bo-title">
                    Salas de subgrupo
                </h2>
                <button
                    type="button"
                    className="vc-bo__close"
                    onClick={onClose}
                    aria-label="Cerrar panel de salas"
                    disabled={isBusy}
                >
                    <XMarkIcon aria-hidden="true" />
                </button>
            </header>

            {blockCreate && (
                <p className="vc-bo__hint" role="status">
                    Las salas no están disponibles en esta sesión. Si el problema continúa, contacta al administrador.
                </p>
            )}

            <div className="vc-bo__body" aria-busy={isBusy}>
                {isBusy && !hasSubsessions ? (
                    <SubsessionsPanelBones />
                ) : (
                    <>
                    {!hasSubsessions && !blockCreate && (
                        <CreateStep
                            count={count}
                            setCount={setCount}
                            pattern={pattern}
                            setPattern={setPattern}
                            busy={isBusy}
                            onCreate={() => run(
                                () => subsessions.createSubsessions(count, pattern),
                                'Salas creadas',
                            )}
                        />
                    )}

                    {hasSubsessions && (
                        <>
                            <section className="vc-bo__toolbar">
                                {!subsessions.isOpen && (
                                    <button
                                        type="button"
                                        className="vc-bo__btn is-primary is-block"
                                        disabled={isBusy || subsessions.isClosing}
                                        onClick={focusAssign}
                                    >
                                        Asignar participantes
                                    </button>
                                )}

                                <div className="vc-bo__toolbar-row">
                                    {!subsessions.isOpen ? (
                                        <button
                                            type="button"
                                            className="vc-bo__btn is-secondary"
                                            disabled={isBusy || subsessions.isClosing}
                                            onClick={() => run(subsessions.openSubsessions, 'Salas abiertas')}
                                        >
                                            <PlayIcon aria-hidden="true" />
                                            {isBusy ? 'Abriendo…' : 'Abrir salas'}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="vc-bo__btn is-danger"
                                            disabled={isBusy}
                                            onClick={() => run(subsessions.closeAllSubsessions, 'Cerrando salas')}
                                        >
                                            <StopIcon aria-hidden="true" />
                                            Cerrar salas
                                        </button>
                                    )}

                                    {subsessions.currentSubsessionId && (
                                        <button
                                            type="button"
                                            className="vc-bo__icon-btn"
                                            disabled={isBusy}
                                            aria-label="Volver a sala principal"
                                            title="Sala principal"
                                            onClick={() => run(subsessions.leaveSubsession)}
                                        >
                                            <ArrowUturnLeftIcon aria-hidden="true" />
                                        </button>
                                    )}

                                    {subsessions.isOpen && (
                                        <button
                                            type="button"
                                            className={`vc-bo__icon-btn ${showBroadcast ? 'is-active' : ''}`}
                                            disabled={isBusy}
                                            aria-label="Mensaje a todas las salas"
                                            aria-pressed={showBroadcast}
                                            title="Mensaje a todas las salas"
                                            onClick={() => setShowBroadcast((v) => !v)}
                                        >
                                            <MegaphoneIcon aria-hidden="true" />
                                        </button>
                                    )}
                                </div>

                                {subsessions.currentSubsessionName && (
                                    <p className="vc-bo__you-are">
                                        Estás en <strong>{subsessions.currentSubsessionName}</strong>
                                    </p>
                                )}
                            </section>

                            {subsessions.helpRequests.length > 0 && (
                                <section className="vc-bo__section is-help" aria-label="Solicitudes de ayuda">
                                    <h4>Piden ayuda</h4>
                                    {subsessions.helpRequests.map((req) => (
                                        <div key={req.userId} className="vc-bo__help">
                                            <span>
                                                <strong>{req.displayName}</strong>
                                                {req.subsessionName ? ` · ${req.subsessionName}` : ''}
                                            </span>
                                            <div className="vc-bo__help-actions">
                                                <button
                                                    type="button"
                                                    className="vc-bo__btn is-small is-primary"
                                                    disabled={isBusy || !req.subsessionId}
                                                    onClick={() => {
                                                        subsessions.dismissHelpRequest(req.userId);
                                                        run(() => subsessions.joinSubsession(req.subsessionId));
                                                    }}
                                                >
                                                    Ir
                                                </button>
                                                <button
                                                    type="button"
                                                    className="vc-bo__btn is-small"
                                                    disabled={isBusy}
                                                    onClick={() => subsessions.postponeHelping(req.userId)}
                                                >
                                                    Posponer
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </section>
                            )}

                            {unassigned.length > 0 && (
                                <section
                                    ref={unassignedRef}
                                    id="vc-bo-unassigned"
                                    className="vc-bo__section"
                                    aria-label="Participantes sin asignar"
                                >
                                    <h4>Sin asignar ({unassigned.length})</h4>
                                    <ul className="vc-bo__members vc-bo__members--flat">
                                        {unassigned.map((member) => (
                                            <li key={`un-${member.userId}`} className="vc-bo__member">
                                                <Avatar name={member.displayName} />
                                                <span className="vc-bo__member-name" title={member.displayName}>
                                                    {member.displayName}
                                                </span>
                                                <label className="vc-bo__sr-only" htmlFor={`assign-${member.userId}`}>
                                                    Asignar a {member.displayName}
                                                </label>
                                                <select
                                                    id={`assign-${member.userId}`}
                                                    className="vc-bo__select"
                                                    value=""
                                                    disabled={isBusy}
                                                    aria-label={`Asignar a ${member.displayName}`}
                                                    onChange={(e) => {
                                                        handleAssign(member.userId, e.target.value);
                                                        e.target.value = '';
                                                    }}
                                                >
                                                    <option value="">Asignar</option>
                                                    {rooms.map((s) => (
                                                        <option key={s.subsessionId} value={s.subsessionId}>
                                                            {s.subsessionName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            <section className="vc-bo__rooms" aria-label="Lista de salas">
                                {rooms.map((sub) => {
                                    const isCurrent = sub.subsessionId === subsessions.currentSubsessionId;
                                    const isCollapsed = Boolean(collapsed[sub.subsessionId]);
                                    const countLabel = sub.userList.length;
                                    return (
                                        <div
                                            key={sub.subsessionId}
                                            className={`vc-bo__room ${isCurrent ? 'is-current' : ''}`}
                                        >
                                            <div className="vc-bo__room-head">
                                                <button
                                                    type="button"
                                                    className="vc-bo__room-toggle"
                                                    aria-expanded={!isCollapsed}
                                                    aria-label={`${isCollapsed ? 'Expandir' : 'Contraer'} ${sub.subsessionName}`}
                                                    onClick={() => toggleRoom(sub.subsessionId)}
                                                >
                                                    {isCollapsed
                                                        ? <ChevronRightIcon aria-hidden="true" />
                                                        : <ChevronDownIcon aria-hidden="true" />}
                                                </button>
                                                <div className="vc-bo__room-meta">
                                                    <span className="vc-bo__room-name">
                                                        {sub.subsessionName}
                                                        {' '}
                                                        <span className="vc-bo__room-count">({countLabel})</span>
                                                    </span>
                                                    <span className={`vc-bo__room-status ${subsessions.isOpen ? 'is-open' : ''}`}>
                                                        {roomStatus}
                                                    </span>
                                                </div>
                                                {subsessions.isOpen && (
                                                    <button
                                                        type="button"
                                                        className="vc-bo__btn is-small"
                                                        disabled={isBusy || isCurrent}
                                                        onClick={() => run(
                                                            () => subsessions.joinSubsession(sub.subsessionId),
                                                        )}
                                                    >
                                                        {isCurrent ? 'Aquí' : 'Entrar'}
                                                    </button>
                                                )}
                                            </div>

                                            {!isCollapsed && (
                                                sub.userList.length > 0 ? (
                                                    <ul className="vc-bo__members">
                                                        {sub.userList.map((member) => (
                                                            <MemberRow
                                                                key={`${sub.subsessionId}-${member.userId}`}
                                                                member={member}
                                                                subsessionId={sub.subsessionId}
                                                                rooms={rooms}
                                                                isOpen={subsessions.isOpen}
                                                                busy={isBusy}
                                                                onAssign={handleAssign}
                                                            />
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="vc-bo__empty">Sin participantes asignados</p>
                                                )
                                            )}
                                        </div>
                                    );
                                })}
                            </section>

                            {subsessions.isOpen && showBroadcast && (
                                <section className="vc-bo__section" aria-label="Mensaje a todas las salas">
                                    <h4>Mensaje a todas las salas</h4>
                                    <div className="vc-bo__broadcast">
                                        <label className="vc-bo__sr-only" htmlFor="vc-bo-broadcast">
                                            Mensaje para todas las salas
                                        </label>
                                        <input
                                            id="vc-bo-broadcast"
                                            type="text"
                                            name="broadcastMessage"
                                            placeholder="Ej. Quedan 5 minutos…"
                                            value={message}
                                            maxLength={200}
                                            autoComplete="off"
                                            spellCheck={false}
                                            onChange={(e) => setMessage(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && message.trim() && !isBusy) {
                                                    e.preventDefault();
                                                    run(async () => {
                                                        await subsessions.broadcast(message);
                                                        setMessage('');
                                                    }, 'Mensaje enviado');
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="vc-bo__btn is-small is-primary"
                                            disabled={isBusy || !message.trim()}
                                            aria-label="Enviar mensaje a todas las salas"
                                            onClick={() => run(async () => {
                                                await subsessions.broadcast(message);
                                                setMessage('');
                                            }, 'Mensaje enviado')}
                                        >
                                            Enviar
                                        </button>
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                    </>
                )}
            </div>
        </aside>
    );
};
